// src/lib/errors/ErrorMessages.ts

import type { ErrorCategory, ErrorReason } from './ErrorClassifier';

export type ErrorAction =
  | 'check_connection'
  | 'retry'
  | 'contact_support'
  | 'reconnect'
  | 'correct_input'
  | 'refresh'
  | 'none';

export interface ErrorPresentation {
  message: string;
  action: ErrorAction;
}

const DEFAULT_MESSAGES: Record<ErrorReason, string> = {
  transport_unreachable:
    'La connexion avec Hector a été interrompue. Vérifiez votre connexion internet et réessayez.',
  transport_timeout:
    "La requête a pris trop de temps. Vérifiez votre connexion et réessayez.",
  server_error:
    "Hector n'a pas pu terminer cette opération. Réessayez. Si l'erreur persiste, contactez-nous via le chat de support.",
  server_unavailable:
    'Hector rencontre momentanément un problème. Réessayez dans quelques instants. Si le problème persiste, contactez-nous via le chat de support.',
  session_expired:
    'Votre session a expiré. Veuillez vous reconnecter pour continuer.',
  forbidden: "Vous n'avez pas les droits pour effectuer cette action. Contactez l'administrateur de votre cabinet ou le chat de support.",
  validation_error:
    "L'opération n'a pas pu aboutir. Vérifiez les informations saisies.",
  not_found: 'Cet élément est introuvable ou a été supprimé. Actualisez la page.',
  conflict: 'Cette opération entre en conflit avec un élément existant. Actualisez la page puis réessayez.',
  rate_limited: 'Trop de demandes simultanées. Patientez quelques instants puis réessayez.',
  business_error:
    "L'opération n'a pas pu aboutir. Vérifiez les informations saisies.",
  stream_interrupted:
    'La réponse a été interrompue. Vérifiez votre connexion puis relancez votre demande.',
  invalid_response:
    "La réponse reçue par Hector n'est pas exploitable. Réessayez. Si l'erreur persiste, contactez-nous via le chat de support.",
  unexpected:
    'Un problème technique est survenu. Réessayez. Si l’erreur persiste, contactez-nous via le chat de support.',
};

const ACTIONS_BY_REASON: Record<ErrorReason, ErrorAction> = {
  transport_unreachable: 'check_connection',
  transport_timeout: 'retry',
  server_error: 'contact_support',
  server_unavailable: 'contact_support',
  session_expired: 'reconnect',
  forbidden: 'contact_support',
  validation_error: 'correct_input',
  not_found: 'refresh',
  conflict: 'refresh',
  rate_limited: 'retry',
  business_error: 'none',
  stream_interrupted: 'retry',
  invalid_response: 'contact_support',
  unexpected: 'contact_support',
};

/**
 * Retourne une cause par défaut pour les appels historiques qui ne fournissent
 * encore que la catégorie et le status HTTP.
 */
function inferReason(
  category: ErrorCategory,
  statusCode?: number
): ErrorReason {
  if (statusCode !== undefined) {
    if (statusCode === 401) return 'session_expired';
    if (statusCode === 403) return 'forbidden';
    if (statusCode === 400 || statusCode === 422) return 'validation_error';
    if (statusCode === 404) return 'not_found';
    if (statusCode === 409) return 'conflict';
    if (statusCode === 429) return 'rate_limited';
    if (statusCode >= 500 && [502, 503, 504].includes(statusCode)) {
      return 'server_unavailable';
    }
    if (statusCode >= 500) return 'server_error';
    if (statusCode >= 400) return 'business_error';
  }

  switch (category) {
    case 'network':
      return 'transport_unreachable';
    case 'auth':
      return 'session_expired';
    case 'business':
      return 'business_error';
    case 'streaming':
      return 'stream_interrupted';
    default:
      return 'unexpected';
  }
}

/**
 * Retourne le message utilisateur correspondant à la cause précise.
 * Le message backend reste prioritaire uniquement pour les validations : c'est
 * le seul cas où il décrit généralement une correction que l'utilisateur peut
 * faire lui-même.
 */
export function getUserMessage(
  category: ErrorCategory,
  backendMessage?: string,
  statusCode?: number,
  reason?: ErrorReason
): string {
  const resolvedReason = reason ?? inferReason(category, statusCode);

  if (
    resolvedReason === 'validation_error' &&
    backendMessage &&
    backendMessage.length > 0
  ) {
    return backendMessage;
  }

  return DEFAULT_MESSAGES[resolvedReason];
}

/**
 * Retourne le message et le prochain geste recommandé à l'interface.
 */
export function getErrorPresentation(
  category: ErrorCategory,
  backendMessage?: string,
  statusCode?: number,
  reason?: ErrorReason
): ErrorPresentation {
  const resolvedReason = reason ?? inferReason(category, statusCode);
  return {
    message: getUserMessage(category, backendMessage, statusCode, resolvedReason),
    action: ACTIONS_BY_REASON[resolvedReason],
  };
}

/**
 * Message spécifique au batch d'upload : il explique ce que l'utilisateur
 * peut vérifier sans laisser croire que Hector est globalement indisponible.
 */
export function getUploadFailureMessage(
  category: ErrorCategory,
  backendMessage?: string,
  statusCode?: number,
  reason?: ErrorReason
): string {
  const resolvedReason = reason ?? inferReason(category, statusCode);

  if (resolvedReason === 'transport_unreachable') {
    return "Certains fichiers n'ont pas pu être importés. Vérifiez votre connexion réseau et rafraîchissez la page de votre navigateur.";
  }
  if (resolvedReason === 'transport_timeout') {
    return 'Certains fichiers n\'ont pas pu être importés car la requête a pris trop de temps. Vérifiez votre connexion et réessayez.';
  }

  return getUserMessage(category, backendMessage, statusCode, resolvedReason);
}
